/**
 * personaGraphAdapter (DC-1648).
 *
 * Maps the canonical `Persona` shape (client/types/triangulation) onto the
 * node/link structure the D3 topology consumes. Replaces the per-component
 * link-building loops the legacy GovernanceTopology and
 * JourneyGovernancePanel each carried:
 *
 *   - links resolve `related_personas[].slug` through a slug→id Map (the
 *     legacy code resolved by label; FAIR personas key on Pistoia slug).
 *   - duplicate edges dedupe via a Set keyed on the sorted id pair —
 *     O(E) instead of the legacy O(E²) `links.some(...)` scan.
 *   - dangling slugs (the Pistoia upstream catalog has 11 cross-references
 *     to personas that don't exist) are silently skipped, matching the
 *     documented RelatedPersonaRef contract.
 */

import type { Persona } from '../../../types/triangulation';
import type { TopologyNode, TopologyLink } from './types';

export interface PersonaGraph {
  nodes: TopologyNode[];
  links: TopologyLink[];
}

export function buildGraph(personas: Persona[]): PersonaGraph {
  const nodes: TopologyNode[] = personas.map((p) => ({
    id: p.id,
    persona: p,
    role: p.primary_role ?? 'unknown',
    isBaseline: p.is_baseline,
    isCritical: p.is_critical,
  }));

  const idBySlug = new Map<string, string>();
  for (const p of personas) {
    if (p.slug) idBySlug.set(p.slug, p.id);
  }

  const nodeIds = new Set(nodes.map((n) => n.id));
  const links: TopologyLink[] = [];
  const seenEdges = new Set<string>();

  for (const p of personas) {
    if (!p.related_personas) continue;
    for (const rel of p.related_personas) {
      const relId = idBySlug.get(rel.slug);
      if (!relId || !nodeIds.has(relId) || relId === p.id) continue;
      const edgeKey = [p.id, relId].sort().join('|');
      if (seenEdges.has(edgeKey)) continue;
      seenEdges.add(edgeKey);
      links.push({ source: p.id, target: relId, type: 'related' });
    }
  }

  return { nodes, links };
}
